import type { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro"

const isSameFilterItem = (
  left: GridFilterItem,
  right: GridFilterItem | undefined
): boolean =>
  right != null &&
  left.id === right.id &&
  left.field === right.field &&
  left.operator === right.operator &&
  left.value === right.value

const isSameQuickFilterValues = (
  left: GridFilterModel["quickFilterValues"],
  right: GridFilterModel["quickFilterValues"]
): boolean => {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return !left && !right
  }
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const isSameFilterModel = (
  left: GridFilterModel,
  right: GridFilterModel
): boolean => {
  if (left === right) {
    return true
  }
  if (left.logicOperator !== right.logicOperator) {
    return false
  }
  if (left.quickFilterLogicOperator !== right.quickFilterLogicOperator) {
    return false
  }
  if (
    left.quickFilterExcludeHiddenColumns !==
    right.quickFilterExcludeHiddenColumns
  ) {
    return false
  }
  if (
    !isSameQuickFilterValues(left.quickFilterValues, right.quickFilterValues)
  ) {
    return false
  }
  if (left.items.length !== right.items.length) {
    return false
  }

  return left.items.every((item, index) =>
    isSameFilterItem(item, right.items[index])
  )
}

/**
 * Keep filters in the order they were added. An item that was already present
 * stays in that position (including when its column changes). New items are
 * appended. Items that were removed are dropped.
 *
 * Matching is by filter item id, so several conditions on one column keep
 * their relative order.
 */
export const preserveAddedFilterItemOrder = (
  previousItems: readonly GridFilterItem[],
  nextItems: readonly GridFilterItem[]
): GridFilterItem[] => {
  const nextById = new Map<GridFilterItem["id"], GridFilterItem>()
  nextItems.forEach((item) => {
    if (item.id != null && !nextById.has(item.id)) {
      nextById.set(item.id, item)
    }
  })

  const usedIds = new Set<GridFilterItem["id"]>()
  const kept = previousItems.flatMap((item) => {
    if (item.id == null || usedIds.has(item.id)) {
      return []
    }
    const nextItem = nextById.get(item.id)
    if (!nextItem) {
      return []
    }
    usedIds.add(item.id)
    return [nextItem]
  })
  const added = nextItems.filter(
    (item) => item.id == null || !usedIds.has(item.id)
  )

  return [...kept, ...added]
}

/**
 * Return `next` with items in added order. Return `current` when that result
 * matches `current`, so a controlled grid echoing the model does not loop.
 */
export const withPreservedFilterItemOrder = (
  current: GridFilterModel,
  next: GridFilterModel
): GridFilterModel => {
  const items = preserveAddedFilterItemOrder(current.items, next.items)
  const ordered =
    items.length === next.items.length &&
    items.every((item, index) => item === next.items[index])
      ? next
      : { ...next, items }

  return isSameFilterModel(current, ordered) ? current : ordered
}
